<script lang="ts" setup>
  import {
    type PageConfig,
    computePageBreakPositions,
    computePageBreakPositionsLegacy,
    DEFAULT_SECTION_OR_MARGIN_HEIGHT,
  } from '@gohighlevel/ghl-proposals-common'
  import { PropType, ref, watch, computed } from 'vue'

  const props = defineProps({
    parentHeight: {
      type: Number,
      default: 0,
      required: true,
    },
    pageDimensions: {
      type: Object as PropType<PageConfig>,
      required: true,
    },
    pageVersion: {
      type: Number,
      default: 1,
      required: true,
    },
    hasHeader: { type: Boolean, default: false },
    hasFooter: { type: Boolean, default: false },
    /** Measured header height (replaces top margin). Persisted on page options. */
    headerHeight: { type: Number, default: undefined },
    /** Measured footer height (replaces bottom margin). Persisted on page options. */
    footerHeight: { type: Number, default: undefined },
  })

  const childElements = ref<number[]>([])

  /** Offset from top of page-view to the start of the content area (header or margin). Used so break lines align with PDF. */
  const contentAreaTopOffset = computed(() => {
    if (props.hasHeader) {
      return props.headerHeight ?? DEFAULT_SECTION_OR_MARGIN_HEIGHT
    }
    return props.pageVersion > 1 ? props.pageDimensions.margins.top : DEFAULT_SECTION_OR_MARGIN_HEIGHT
  })

  function updatePageBreaks() {
    const input = {
      parentHeight: props.parentHeight,
      hasHeader: props.hasHeader,
      hasFooter: props.hasFooter,
      headerHeight: props.headerHeight,
      footerHeight: props.footerHeight,
    }
    if (props.pageVersion > 1) {
      childElements.value = computePageBreakPositions({
        ...input,
        pageDimensions: props.pageDimensions,
      })
    } else {
      childElements.value = computePageBreakPositionsLegacy(input)
    }
  }

  watch(
    () => [
      props.parentHeight,
      props.pageDimensions,
      props.pageVersion,
      props.hasHeader,
      props.hasFooter,
      props.headerHeight,
      props.footerHeight,
    ],
    () => updatePageBreaks(),
    { immediate: true }
  )
</script>

<template>
  <div v-for="(child, index) in childElements" :key="index" class="child">
    <div class="pg-break" :style="`top:calc(${contentAreaTopOffset + child - 22}px)`">
      {{ $t('common.pdfPageBreak') }}
    </div>
  </div>
</template>

<style lang="scss">
  .pg-break {
    position: absolute;
    width: 100px;
    left: -104px;
    border-top: 2px dotted gray;
    color: rgb(141, 141, 141);
    font-size: 10px;
    padding: 2px;
    user-select: none;
    font-weight: 600;
  }
</style>
